import React, { useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  CircularProgress,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import { AccountData, VoucherOut, VoucherService, VoucherStatus, VoucherType } from '../../../../../api';
import { useAuth } from '../../../../../session/InternalAuthProvider';
import { customerFacingPriceFormatter } from '../../../../../utils/utils';

const T_PREFIX = 'calendar.edit_order.order_details.extraPayment';
const SEARCH_DEBOUNCE_MS = 300;
const MAX_OPTIONS = 8;

enum VoucherSource {
  ORDER = 'order',
  OTHER = 'other',
}

type MonetaryVoucherPickerProps = {
  orderVoucherId?: string;
};

type PickerFormValues = { voucherId: string; voucherBalance?: number; price: number | '' };

export const MonetaryVoucherPicker = ({ orderVoucherId }: MonetaryVoucherPickerProps) => {
  const { t } = useTranslation();
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const { values, setFieldValue, touched, errors } = useFormikContext<PickerFormValues>();
  const [orderVoucher, setOrderVoucher] = useState<VoucherOut | undefined>(undefined);
  const [voucherSource, setVoucherSource] = useState<VoucherSource>(VoucherSource.OTHER);
  const [searchedVoucher, setSearchedVoucher] = useState<VoucherOut | null>(null);
  const [search, setSearch] = useState('');
  const [options, setOptions] = useState<VoucherOut[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const selectedVoucher = voucherSource === VoucherSource.ORDER ? orderVoucher : searchedVoucher;

  const formatPrice = (amount: number) => customerFacingPriceFormatter(amount, account);
  const voucherLabel = (voucher: VoucherOut) =>
    t(`${T_PREFIX}.voucherOption`, {
      name: `${voucher.firstname} ${voucher.lastname}`,
      orderNumber: voucher.order_number,
      balance: formatPrice(voucher.balance ?? 0),
    });

  useEffect(() => {
    void setFieldValue('voucherId', selectedVoucher?.id ?? '');
    void setFieldValue('voucherBalance', selectedVoucher?.balance ?? undefined);
  }, [selectedVoucher?.id]);

  useEffect(() => {
    if (!orderVoucherId) {
      return;
    }
    void VoucherService.getVoucherById(orderVoucherId).then((voucher) => {
      if (voucher.type === VoucherType.MONETARY && voucher.status === VoucherStatus.ACTIVE && voucher.balance) {
        setOrderVoucher(voucher);
        setVoucherSource(VoucherSource.ORDER);
      }
    });
  }, [orderVoucherId]);

  useEffect(() => {
    if (voucherSource !== VoucherSource.OTHER) {
      return undefined;
    }
    setIsLoading(true);
    const timeout = setTimeout(async () => {
      const vouchers = await VoucherService.getVouchers(
        1,
        MAX_OPTIONS,
        search || undefined,
        [VoucherStatus.ACTIVE],
        [],
        [],
        [VoucherType.MONETARY],
      );
      setOptions(vouchers.filter((voucher) => voucher.id !== orderVoucher?.id));
      setIsLoading(false);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timeout);
  }, [search, voucherSource, orderVoucher?.id]);

  const searchField = (
    <Autocomplete
      size="small"
      options={options}
      value={searchedVoucher}
      loading={isLoading}
      filterOptions={(voucherOptions) => voucherOptions}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      getOptionLabel={voucherLabel}
      onChange={(_, voucher) => setSearchedVoucher(voucher)}
      onInputChange={(_, value, reason) => {
        if (reason === 'input') {
          setSearch(value);
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={t(`${T_PREFIX}.voucherCode`)}
          error={touched.voucherId && Boolean(errors.voucherId)}
          helperText={touched.voucherId && errors.voucherId}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isLoading && <CircularProgress color="inherit" size={16} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );

  const price = Number(values.price) || 0;
  const balance = selectedVoucher?.balance ?? 0;

  return (
    <Grid container flexDirection="column" gap={1} sx={{ paddingInlineStart: 4, mb: 1 }}>
      {orderVoucher ? (
        <RadioGroup value={voucherSource} onChange={(event) => setVoucherSource(event.target.value as VoucherSource)}>
          <FormControlLabel
            value={VoucherSource.ORDER}
            control={<Radio size="small" />}
            label={t(`${T_PREFIX}.orderVoucherOption`, { voucher: voucherLabel(orderVoucher) })}
          />
          <FormControlLabel
            value={VoucherSource.OTHER}
            control={<Radio size="small" />}
            label={t(`${T_PREFIX}.otherVoucherOption`)}
          />
        </RadioGroup>
      ) : (
        <Typography variant="body2" color="text.secondary">
          {t(`${T_PREFIX}.noOrderVoucher`)}
        </Typography>
      )}
      {voucherSource === VoucherSource.OTHER && searchField}
      {selectedVoucher && price > 0 && (
        <Alert severity={price > balance ? 'error' : 'info'}>
          {price > balance
            ? t(`${T_PREFIX}.errors.voucherBalanceTooLow`)
            : t(`${T_PREFIX}.voucherDeductionSummary`, {
                amount: formatPrice(price),
                orderNumber: selectedVoucher.order_number,
                balanceAfter: formatPrice(balance - price),
              })}
        </Alert>
      )}
    </Grid>
  );
};
